import { BLUETOOTH } from '@/app.config';
import AppError from '@/services/error.service';

/*
 * Check if bluetooth is Enabled
 *
 * @return {Promise}           - Promise object with result of check if device bluetooth is enabled
 * @throws {bluetoothDisabled} - Device bluetooth is Disabled
 */
function isBluetoothEnabled() {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    ble.isEnabled(
      () => resolve(true),
      () => {
        reject(new AppError('bluetoothDisabled', 'Device bluetooth is Disabled'));
      }
    );
  });
}

/*
 * Check if location is Enabled
 *
 * iOS device activate by default the location when the bluetooth is activated
 *
 * @return {Promise}              - Promise object with result of check if device location is enabled
 * @throws {LocationDisabled}     - Device location is Disabled
 * @throws {errorLocationEnabled} - Error trying to check if the device location is Enabled
 */
function isLocationEnabled() {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    if (device.platform === 'iOS') {
      resolve(true);
      return;
    }

    // eslint-disable-next-line
    cordova.plugins.diagnostic.isLocationEnabled(
      enabled => {
        if (enabled) {
          resolve(true);
        } else {
          reject(new AppError('LocationDisabled', 'Device location is Disabled'));
        }
      },
      error => {
        reject(new AppError('errorLocationEnabled', error));
      }
    );
  });
}

/*
 * Check if location is Available
 *
 * iOS device activate by default the location when the bluetooth is activated
 *
 * @return {Promise}                - Promise object with result of check if device location is available
 * @throws {LocationNotAvailable}   - Device location is not Available
 * @throws {errorLocationAvailable} - Error trying to check if the device location is Available
 * @throws {errorAskActiveLocation} - Error trying to ask the user if activate the location
 */
function isLocationAvailable() {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    if (device.platform === 'iOS') {
      resolve(true);
      return;
    }

    // eslint-disable-next-line
    cordova.plugins.diagnostic.isLocationAvailable(
      available => {
        if (available) {
          resolve(true);
        } else {
          // Ask the user if he want available location
          // eslint-disable-next-line
          cordova.plugins.diagnostic.requestLocationAuthorization(
            status => {
              if (
                // eslint-disable-next-line
                status === cordova.plugins.diagnostic.permissionStatus.GRANTED
              ) {
                resolve(true);
              } else {
                reject(new AppError('LocationNotAvailable', 'Device location isn´t Available'));
              }
            },
            error => {
              reject(new AppError('errorAskActiveLocation', error));
            }
          );
        }
      },
      error => {
        reject(new AppError('errorLocationAvailable', error));
      }
    );
  });
}

/*
 * Search devices by bluetooth
 *
 * The succees method is called every time a new device is founded
 *
 * @constant {MIN_LEVEL_SIGNAL}      - Minimum level of the signal, avoid showing devices too far away that can fail in communications
 * @constant {MAX_TIME_SCAN_DEVICES} - Device search time
 * @param  {function}                - Callback function when a new device is found
 * @return {Promise}                 - Promise object with each device founded
 * @throws {errorStartScan}          - Error trying to scan devices by bluetooth
 * @throws {errorStopScan}           - Error trying to stop scan devices by bluetooth
 */
function scanDevicesBluetooth(prefixes, callback) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    ble.startScan(
      [],
      device => {
        if (
          device.name &&
          prefixes.some(prefix => device.name.startsWith(prefix)) &&
          device.rssi > BLUETOOTH.MIN_LEVEL_SIGNAL
        ) {
          // Change name property to fix conflict when associate device
          device.deviceName = device.name;
          device.name = '';
          callback(device);
        }
      },
      () => {
        reject(new AppError('errorStartScan', 'Error trying to scan devices by bluetooth'));
      }
    );

    /*
     * Stop scan devices after time
     */
    setTimeout(() => {
      // eslint-disable-next-line
      ble.stopScan(
        () => {
          resolve(true);
        },
        () => {
          // prettier-ignore
          reject(new AppError('errorStopScan', 'Error trying to try stop scan devices by bluetooth'));
        }
      );
    }, BLUETOOTH.MAX_TIME_SCAN_DEVICES);
  });
}

/*
 * Connect to device
 *
 * @param  {strint}           - Device id
 * @return {Promise}          - Promise with result of connect device
 * @throws {disconnectDevice} - Device disconnects or an error occurs
 */
function connectDevice(deviceID) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    ble.isConnected(
      deviceID,
      () => resolve(true),
      () => {
        // eslint-disable-next-line
        ble.connect(
          deviceID,
          () => resolve(true),
          () => {
            reject(new AppError('disconnectDevice', 'Device disconnects or an error occurs'));
          }
        );
      }
    );
  });
}

/*
 * Convert ArrayBuffer to string
 *
 * @param  {ArrayBuffer}        - Array Buffer with data from device
 * @return {JSON}               - JSON with data from device
 * @throws {errorBytesToString} - Error when try pass bytes to string
 */
function bytesToString(buffer) {
  return new Promise(resolve => {
    try {
      const string = String.fromCharCode.apply(null, new Uint8Array(buffer));
      const clearString = string.replace(/\n/g, '');
      resolve(JSON.parse(clearString));
    } catch (error) {
      /*
       En algunas ocasiones, mientras estoy recibiendo datos, el WS puede cambiar de estado
       mezlando la respuesta, y generándose un JSON mal formado, enviamos un objeto vacío para
       que vuelva a realizar la petición, que ya estará bien.
       */
      const response = {};
      resolve(response);
      // reject(new AppError('errorBytesToString', 'Error in transform bytes to string'));
    }
  });
}

/*
 * Convert data to ArrayBuffer
 *
 * @param  {String}             - String with data
 * @return {ArrayBuffer}        - Array Buffer with data converted
 * @throws {errorStringToBytes} - Error when try pass string to bytes
 */
function stringToBytes(string) {
  return new Promise((resolve, reject) => {
    try {
      const array = new Uint8Array(string.length);

      for (let i = 0, l = string.length; i < l; i += 1) {
        array[i] = string.charCodeAt(i);
      }

      resolve(array.buffer);
    } catch (error) {
      reject(new AppError('errorStringToBytes', 'Error in transform string to bytes'));
    }
  });
}

/*
 * Write to device by Bluetooth
 *
 * @param  {String}           - Device ID
 * @param  {String}           - Command to send device
 * @return {Promise}          - Result of write device
 * @throws {errorWriteDevice} - Error when try write to device
 */
function writeDevice(deviceID, command) {
  return new Promise(async (resolve, reject) => {
    const dataBuffer = await stringToBytes(command).catch(error => reject(error));

    console.log('WRITE ===================>');
    console.log('deviceID', deviceID);
    console.log('SERVICE_UUID', BLUETOOTH.SERVICE_UUID);
    console.log('CHARACTERISTIC_UUID', BLUETOOTH.CHARACTERISTIC_UUID);
    console.log('command', command);

    // eslint-disable-next-line
    ble.write(
      deviceID,
      BLUETOOTH.SERVICE_UUID,
      BLUETOOTH.CHARACTERISTIC_UUID,
      dataBuffer,
      () => {
        console.warn('write device SUCCESS');
        resolve(true);
      },
      async error => {
        console.error('write device ERROR:', error);
        await connectDevice(deviceID);
        resolve(true);
        // if (error === 'Peripheral disconnected') {
        //   await connectDevice(deviceID);
        //   resolve(true);
        // } else {
        //   reject(new AppError('errorWriteDevice', error));
        // }
      }
    );
  });
}

/*
 * Read characteristic from device by Bluetooth
 *
 * @param  {String}          - Device ID
 * @return {Promise}         - Result of read device
 * @throws {errorReadDevice} - Error when try read from device
 */
function readDevice(deviceID) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line
    ble.read(
      deviceID,
      BLUETOOTH.SERVICE_UUID,
      BLUETOOTH.CHARACTERISTIC_UUID,
      async data => {
        const deviceInfo = await bytesToString(data).catch(error => reject(error));
        resolve(deviceInfo);
      },
      error => {
        reject(new AppError('errorReadDevice', error));
      }
    );
  });
}

/*
 * Stops the program flow for a while
 *
 * @param  {Integer} - Number of miliseconds
 * @return {Promise} - Return when timeout is finished
 */
// function sleep(miliseconds) {
//   return new Promise(resolve => setTimeout(resolve, miliseconds));
// }

function getDeviceData(deviceID, dataInfo, callback) {
  return new Promise(async (resolve, reject) => {
    let breakLoop = false;

    const timeout = setTimeout(() => {
      breakLoop = true;
    }, BLUETOOTH.MAX_TIME_GET_DATA);

    try {
      console.log(`============== ${dataInfo} =================`);

      const getData = async attempts => {
        const deviceInfo = await readDevice(deviceID); // eslint-disable-line
        console.log(`Attempts: ${attempts}`);
        console.log(deviceInfo);

        if (dataInfo === 'getWsServices' && deviceInfo.services) {
          clearTimeout(timeout);
          resolve(deviceInfo);
          return;
        }

        if (dataInfo === 'setServices' && deviceInfo.services) {
          console.log('Resp setServices', deviceInfo);

          clearTimeout(timeout);
          resolve(deviceInfo);
          return;
        }

        if (dataInfo === 'getDeviceParams' && deviceInfo.get) {
          clearTimeout(timeout);
          resolve(null);
          return;
        }

        if (dataInfo === 'getDeviceParams' && deviceInfo.info) {
          clearTimeout(timeout);
          resolve(deviceInfo.info);
          return;
        }

        if (dataInfo === 'getDeviceInfo' && deviceInfo.general) {
          clearTimeout(timeout);
          resolve(deviceInfo);
          return;
        }

        if (dataInfo === 'getExtraInfo' && deviceInfo?.extrainfo) {
          clearTimeout(timeout);
          resolve(deviceInfo);
          return;
        }

        if (dataInfo === 'getDeviceWifis' && deviceInfo.aplist) {
          clearTimeout(timeout);
          resolve(deviceInfo.aplist);
          return;
        }

        if (dataInfo === 'connectDeviceToWifi' && deviceInfo.general) {
          callback(deviceInfo);

          if (
            deviceInfo.wifi.status === 0 ||
            deviceInfo.wifi.status === 3 ||
            deviceInfo.wifi.status === 4
          ) {
            clearTimeout(timeout);
            resolve(true);
            return;
          }
        }

        if (!breakLoop) {
          setTimeout(() => {
            getData(attempts + 1);
          }, BLUETOOTH.SLEEP_TIME_BETWEEN_REQUEST);
        } else {
          console.error('errorGetDeviceInfo');
          reject(new AppError('errorGetDeviceInfo', 'Timeout when try get device data'));
        }
      };

      getData(1);
    } catch (error) {
      clearTimeout(timeout);
      reject(error);
    }
  });
}

const BluetoothService = {};

/**
 * Get a list of devices available by Bluetooth.
 *
 * First check if the user has the bluetooth and location enabled.
 * The search for devices via bluetooth launches an event when it finds a new one,
 * we use a callback function to manage each device when it's found.
 *
 * @param  {function} callback      - Callback function when a new device is found
 * @return {array} devices          - List of found devices
 * @throws {bluetoothDisabled}      - Device bluetooth is Disabled
 * @throws {LocationDisabled}       - Device location is Disabled
 * @throws {errorLocationEnabled}   - Error trying to check if the device location is Enabled
 * @throws {LocationNotAvailable}   - Device location is not Available
 * @throws {errorLocationAvailable} - Error trying to check if the device location is Available
 * @throws {errorAskActiveLocation} - Error trying to ask the user if activate the location
 * @throws {errorStartScan}         - Error trying to scan devices by bluetooth
 * @throws {errorStopScan}          - Error trying to stop scan devices by bluetooth
 */
BluetoothService.getDevicesBluetooth = async (prefixes, callback) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  return scanDevicesBluetooth(prefixes, callback);
};

/**
 * Get info of a device
 *
 * @param  {string} deviceID     - ID of device
 * @return {object}              - Device data
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.getDeviceInfo = async deviceID => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, '{"cmd":"info"}');
  let response = await getDeviceData(deviceID, 'getDeviceInfo');

  if (response && response.general && response.general.ble_version >= 3) {
    await writeDevice(deviceID, '{"cmd":"get_extrainfo"}');
    const responseExtraInfo = await getDeviceData(deviceID, 'getExtraInfo');
    response = { ...response, ...responseExtraInfo };
  }

  return response;
};

/**
 * Get parameters of a device
 *
 * @param  {string} deviceID     - ID of device
 * @param  {array} params        - Parameters
 * @return {object}              - Device data
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceParams}- Error when try get device parameters
 */
BluetoothService.getDeviceParams = async (deviceID, params) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  const command = {};
  command.get = {};
  command.get.params = params;
  await writeDevice(deviceID, JSON.stringify(command));
  return getDeviceData(deviceID, 'getDeviceParams');
};

/**
 * Set Parameter to device
 *
 * @param  {string} deviceID     - ID of device
 * @param  {object} data         - Parameters
 * @return {object}              - Device data
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorSetDeviceParam}- Error when try set device parameter
 */
BluetoothService.setDeviceParam = async (deviceID, data) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, `{"set":{"params":${data}}}`);
  return getDeviceData(deviceID, 'getDeviceParams');
};

/**
 * Set Protocol data
 *
 * @param  {string} deviceID     - ID of device
 * @param  {object} data         - Protocol data
 * @return {object}              - Device data
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.setExtrainfo = async (deviceID, data) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, `{"extrainfo":${data}}`);
  // await writeDevice(deviceID, `{"info":${data}}`);
  return BluetoothService.getDeviceInfo(deviceID);
};

/**
 * Get services to device
 *
 * @param  {string} deviceID     - ID of device
 * @return {object}              - Device data
 * @throws  {unableConnectDevice}   - Unable connect to device
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.getWsServices = async deviceID => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, '{"cmd":"getservices"}');
  return getDeviceData(deviceID, 'getWsServices');
};

/**
 * Set services to device
 *
 * @param  {string} deviceID     - ID of device
 * @param {Object} params        - Services data
 * @return {object}              - Device data
 * @throws  {unableConnectDevice}   - Unable connect to device
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.setWsService = async (deviceID, params) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, `{"setservices": { "services": ${JSON.stringify(params)}} }`);
  return getDeviceData(deviceID, 'setServices');
};

/*
 * Test a device
 *
 * @param  {string} - ID of device
 * @param  {string} - Action command
 * @return {booelan} - Result of apply action
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.testDevice = async (deviceID, action) => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, `{"cmd":"${action}"}`);
  return true;
};

/*
 * Get WiFi networks available from a device
 *
 * @param  {string} - ID of device
 * @return {object} - Device data
 * @throws  {disconnectDevice}   - Device disconnects or an error occurs
 * @throws  {errorWriteDevice}   - Error when try write to device
 * @throws  {errorStringToBytes} - Error when try pass string to bytes
 * @throws  {errorBytesToString} - Error when try pass bytes to string
 * @throws  {errorReadDevice}    - Error when try read from device
 * @throws  {errorGetDeviceInfo} - Error when try get device info
 */
BluetoothService.getDeviceWifis = async deviceID => {
  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, '{"cmd":"scan"}');
  return getDeviceData(deviceID, 'getDeviceWifis');
};

/*
 * Clear device to WiFi Network
 *
 * @param  {string}             - ID of device
 * @param  {function}           - Callback function when device finish connect
 * @return {object}             - Device data
 * @throws {disconnectDevice}   - Device disconnects or an error occurs
 * @throws {errorWriteDevice}   - Error when try write to device
 * @throws {errorStringToBytes} - Error when try pass string to bytes
 * @throws {errorBytesToString} - Error when try pass bytes to string
 * @throws {errorReadDevice}    - Error when try read from device
 * @throws {errorGetDeviceInfo} - Error when try get device info
 */
// prettier-ignore
BluetoothService.clearWifi = async (deviceID, callback) => {
  const wifiSetting = '"wifi":{"ssid":" ","bssid":" ","password":" "}'; // eslint-disable-line
  const networkSetting = '"network":{"dhcp":1},"ip":"","mask":"","gateway":"","dns":""}'; // eslint-disable-line
  const command = `{${wifiSetting},${networkSetting},"cmd":"connect"}`;

  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, command);
  return getDeviceData(deviceID, 'connectDeviceToWifi', callback);
};

/*
 * Connect device to WiFi Network
 *
 * @param  {string}             - ID of device
 * @param  {object}             - WiFi Network data
 * @param  {object}             - Data to connect with WiFi Network
 * @param  {function}           - Callback function when device finish connect
 * @return {object}             - Device data
 * @throws {disconnectDevice}   - Device disconnects or an error occurs
 * @throws {errorWriteDevice}   - Error when try write to device
 * @throws {errorStringToBytes} - Error when try pass string to bytes
 * @throws {errorBytesToString} - Error when try pass bytes to string
 * @throws {errorReadDevice}    - Error when try read from device
 * @throws {errorGetDeviceInfo} - Error when try get device info
 */
// prettier-ignore
BluetoothService.connectDeviceToWifi = async (deviceID, wifi, setting, callback) => {
  const wifiSetting = `"wifi":{"ssid":"${wifi.ssid}","bssid":"${wifi.bssid}","password":"${encodeURIComponent(wifi.password)}"}`; // eslint-disable-line
  const networkSetting = `"network":{"dhcp":${setting.dhcp ? 1 : 0},"ip":"${setting.ip}","mask":"${setting.mask}","gateway":"${setting.gateway}","dns":"${setting.dns}"}`; // eslint-disable-line
  const command = `{${wifiSetting},${networkSetting},"cmd":"connect"}`;

  await isBluetoothEnabled();
  await isLocationEnabled();
  await isLocationAvailable();
  await connectDevice(deviceID);
  await writeDevice(deviceID, command);
  return getDeviceData(deviceID, 'connectDeviceToWifi', callback);
};

/*
 * Disconnect bluetooth device
 *
 * @param  {string}                - ID of device
 * @return {boolean}               - Return true if device is disconnected
 * @throws  {errorDisconnectDevice} - Failed to disconnect device
 */
// prettier-ignore
BluetoothService.disconnectDevice = deviceID => {
  // eslint-disable-next-line
  ble.isConnected(deviceID, () => {
    // eslint-disable-next-line
    ble.disconnect(
      deviceID,
      () => true,
      () => {
        throw new AppError('errorDisconnectDevice', 'Failed to disconnect device');
      },
    );
  });
};

export default BluetoothService;
